'use client';

import { useState, useCallback } from 'react';
import { NEXT_MUTATION_ENTITY, EntityMutationResponseMap } from '@common/interfaces';
import { getTokenLocalStorage } from '@config/authentication';
import { mutateNextEntityWithToken } from '@utils/serverActions/clientUtility';
import { useAppSelector } from '@store/hooks';
import { selectOrderId } from '@store/selectors';

export interface IUseClientMutationOptions<MAPPED_ENTITY extends NEXT_MUTATION_ENTITY> {
  onCompleted?: (data: EntityMutationResponseMap[MAPPED_ENTITY]) => void;
  onError?: (error: any) => void;
}

export interface IUseClientMutationResult<MAPPED_ENTITY extends NEXT_MUTATION_ENTITY> {
  data: EntityMutationResponseMap[MAPPED_ENTITY] | null;
  loading: boolean;
  error: any;
}

type MutationExecutor<MAPPED_ENTITY extends NEXT_MUTATION_ENTITY> = (
  variables?: Record<string, any>,
  nextOptions?: RequestInit,
  orderId?: string
) => Promise<EntityMutationResponseMap[MAPPED_ENTITY] | null>;

/**
 * useClientMutation — a custom hook mimicking Apollo's useMutation.
 *
 * Example:
 * const [createUser, { data, loading, error }] = useClientMutation(NEXT_ENTITY.USER, {
 *   onCompleted: (data) => console.log('User created', data),
 *   onError: (err) => console.error('Error', err)
 * });
 *
 * await createUser({ name: 'John' });
 */
export function useClientMutation<MAPPED_ENTITY extends NEXT_MUTATION_ENTITY>(
  entity: MAPPED_ENTITY,
  options: IUseClientMutationOptions<MAPPED_ENTITY> = {}
): [MutationExecutor<MAPPED_ENTITY>, IUseClientMutationResult<MAPPED_ENTITY>] {
  const { onCompleted, onError } = options;

  const [data, setData] = useState<EntityMutationResponseMap[MAPPED_ENTITY] | null>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const nextOrderId = useAppSelector(selectOrderId);

  const executeMutation: MutationExecutor<MAPPED_ENTITY> = useCallback(
    async (variables = {}, nextOptions = { cache: 'no-store' }) => {
      setLoading(true);
      try {
        const result = await mutateNextEntityWithToken(
          entity,
          getTokenLocalStorage(),
          variables,
          nextOptions,
          nextOrderId
        );
        setData(result);
        setError(null);
        onCompleted?.(result);
        return result;
      } catch (err) {
        setError(err);
        onError?.(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [entity, onCompleted, onError]
  );

  return [executeMutation, { data, loading, error }];
}
